import {
    areArrayEquals,
    areRecordsEquals,
    type Comparators,
    createArrayComparator,
    createEqualityChecker,
    createNamedEqualityChecker,
    isArray,
    type ReadonlyRecord,
} from '../../utils'

import { StatusType } from '..'
import {
    areCybusAvailableResourcesEquals,
    areCybusConfiguredResourcesEquals,
    type CybusAvailableResources,
    type CybusConfiguredResources,
    type CybusServiceParameters,
} from '.'

type CybusServiceLink = Readonly<{ name: string, href: string }>

type FullCybusService = Readonly<{
    id: string
    name: string
    version: string | null
    status: StatusType
    deviation: StatusType.DEVIATED | StatusType.NOT_ENABLED | StatusType.NO_DEVIATION

    icon: string | null
    description: string
    provider: string | null

    parameters: CybusServiceParameters

    configuredResources: CybusConfiguredResources

    /**
     * The resources that are actually enabled
     */
    availableResources: CybusAvailableResources

    /**
     * The configured links
     */
    links: readonly CybusServiceLink[]

    resourceDeviationsCount: number

    /**
     * The services dependencies related to this service
     */
    dependencies: CybusServiceDependencies
}>

export type CybusDeviatedService = Pick<FullCybusService, 'id' | 'name' | 'version' | 'resourceDeviationsCount'>
export type CybusService = Pick<FullCybusService, 'id' | 'name' | 'version' | 'status' | 'deviation' | 'links'>
export type CybusLinkedService = Pick<FullCybusService, 'id' | 'name' | 'links'>
export type CybusDetailedService = Pick<FullCybusService, keyof FullCybusService>
type CybusServiceDependencies = ReadonlyRecord<'dependsOn' | 'dependent', readonly FullCybusService['id'][]>

export const areCybusDeviatedServiceEquals = createNamedEqualityChecker<CybusDeviatedService>(
    { id: null, name: null, version: null, resourceDeviationsCount: null },
    'areCybusDeviatedServiceEquals'
)

const areCybusServiceLinkEquals = createEqualityChecker<CybusServiceLink>({ name: null, href: null })

const cybusServiceComparators: Comparators<CybusService> = {
    id: null,
    name: null,
    version: null,
    status: null,
    deviation: null,
    links: createArrayComparator(areCybusServiceLinkEquals),
}

export const areCybusServiceEquals = createNamedEqualityChecker<CybusService>(cybusServiceComparators, 'areCybusServiceEquals')

export const areServiceParametersEquals = (a: CybusDetailedService['parameters'], b: CybusDetailedService['parameters']): boolean =>
    areRecordsEquals(a, b, { equals: (pA, pB) => (isArray(pA) && isArray(pB) ? areArrayEquals(pA, pB, { sort: false }) : pA === pB) })

export const areDetailedCybusServiceEquals = createNamedEqualityChecker<CybusDetailedService>(
    {
        ...cybusServiceComparators,
        icon: null,
        provider: null,
        description: null,
        parameters: areServiceParametersEquals,
        configuredResources: areCybusConfiguredResourcesEquals,
        availableResources: areCybusAvailableResourcesEquals,
        resourceDeviationsCount: null,
        dependencies: createEqualityChecker<CybusServiceDependencies>({ dependsOn: createArrayComparator(), dependent: createArrayComparator() }),
    },
    'areDetailedCybusServiceEquals'
)

export const areLinkedCybusServiceEquals = createNamedEqualityChecker<CybusLinkedService>(
    { id: null, name: null, links: createArrayComparator(areCybusServiceLinkEquals) },
    'areLinkedCybusServiceEquals'
)

export const canServiceBeEnabled = (service: Pick<CybusService, 'status'>): boolean => service.status === StatusType.DISABLED
export const canServiceBeDisabled = (service: Pick<CybusService, 'status'>): boolean => service.status === StatusType.ENABLED

export const doesServiceNeedAuthorization = ({ configuredResources }: CybusDetailedService): boolean => Object.values(configuredResources).some((r) => r > 0)
